import { Dispatch, SetStateAction, useEffect } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/Button"
import { CrossIcon } from "@/components/icons/CrossIcon"
import { isiOS, isMobileDevice, isPWA } from "@/utils"

import { PWABanner } from "./PWABanner"
import { PWAInstallModal } from "./PWAInstallModal"
import { usePWABannerPrompt } from "./usePWABannerPrompt"

interface InstallBannerProps {
  banner: string | null
  updateBanner: (state: PWABanner) => void
  isModalOpen: boolean
  setIsModalOpen: Dispatch<SetStateAction<boolean>>
}

export const PWAInstallBanner = ({
  banner,
  updateBanner,
  isModalOpen,
  setIsModalOpen,
}: InstallBannerProps) => {
  const [prompt, promptToInstall] = usePWABannerPrompt()
  const { t } = useTranslation()

  const isHidden = banner !== PWABanner.Shown || (!prompt && !isiOS) || isPWA()

  useEffect(() => {
    if (prompt && banner === PWABanner.NotSet) {
      updateBanner(PWABanner.Shown)
    } else if (isiOS && banner === PWABanner.NotSet) {
      updateBanner(PWABanner.Shown)
    }
  }, [prompt, banner, updateBanner])

  useEffect(() => {
    const handler = () => {
      updateBanner(PWABanner.Installed)
    }

    window.addEventListener("appinstalled", handler)

    return () => window.removeEventListener("appinstalled", handler)
  }, [updateBanner])

  const closeBanner = () => {
    updateBanner(PWABanner.Hidden)
  }

  const installPWA = (isiOS: boolean) => {
    if (isiOS) {
      setIsModalOpen(true)
    } else {
      promptToInstall()
    }
  }

  if (isModalOpen) {
    return <PWAInstallModal closeModal={() => setIsModalOpen(false)} />
  }

  return (
    <div
      className={`${isHidden ? "hidden" : "flex"} z-[100] h-[32px] w-full items-center gap-md bg-primary-solid px-lg py-0`}
    >
      <div
        className="text-primary_alt hover:cursor-pointer"
        onClick={closeBanner}
        role="button"
        aria-label={t("pwa.closeBanner")}
      >
        <CrossIcon height={10} />
      </div>
      <p className="text-md-semibold text-primary_alt">
        {isMobileDevice
          ? t("pwa.installPrompt.mobile")
          : t("pwa.installPrompt.desktop")}
      </p>
      <Button variant="brand" size="sm" onClick={() => installPWA(isiOS)}>
        {t("pwa.install")}
      </Button>
    </div>
  )
}
